import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import type { ErpModule } from '@/api/platform.api';
import { resolveModuleFromPath } from '@/config/appNavigation';
import { isPlatformAdminRoute } from '@/config/moduleDefaults';
import { isGlobalWorkspaceRoute } from '@/constants/globalWorkspaceRoutes';
import { isStaleModulePath } from '@/utils/moduleNavigation';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import {
  getLauncherModules,
  isOrgOwner,
  resolveDefaultAccessibleWorkspaceModule,
} from '@/utils/moduleAccess';
import {
  selectAccessibleModules,
  selectEnabledModules,
} from '@/store/authSelectors';
import { usePermissions } from '@/hooks/usePermissions';
import { setWorkspaceModule } from '@/store/authSlice';

/**
 * Active application for sidebar and app launcher.
 * Org owners: workspaceModule (switch without re-login). Scoped users: entryModule.
 * Always constrained to launcher-accessible modules for this user.
 */
export function useActiveModule(): ErpModule | null {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const workspaceModule = useAppSelector((s) => s.auth.workspaceModule);
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const enabledModules = useAppSelector(selectEnabledModules);
  const accessibleModules = useAppSelector(selectAccessibleModules);
  const authLoading = useAppSelector((s) => s.auth.loading);
  const { has, permissions, roleCode } = usePermissions();
  const owner = isOrgOwner(permissions, roleCode);

  const launcherCodes = useMemo(
    () =>
      getLauncherModules(enabledModules, permissions, accessibleModules, roleCode, has).map(
        (entry) => entry.erpModule,
      ),
    [enabledModules, permissions, accessibleModules, roleCode, has],
  );

  const inferred = useMemo(
    () => resolveModuleFromPath(location.pathname),
    [location.pathname],
  );

  const fallback = useMemo(
    () =>
      resolveDefaultAccessibleWorkspaceModule(
        enabledModules,
        permissions,
        accessibleModules,
        roleCode,
        has,
      ),
    [enabledModules, permissions, accessibleModules, roleCode, has],
  );

  const sanitize = (module: ErpModule | null | undefined): ErpModule | null => {
    if (!module) return null;
    if (launcherCodes.includes(module)) return module;
    return fallback;
  };

  const rawActive = owner
    ? (workspaceModule ?? inferred ?? fallback)
    : (entryModule ?? workspaceModule ?? inferred ?? fallback);

  const activeModule = sanitize(rawActive);

  useEffect(() => {
    if (authLoading) return;
    if (!activeModule) return;
    if (owner) {
      if (isPlatformAdminRoute(location.pathname)) return;
      if (isGlobalWorkspaceRoute(location.pathname)) return;
      if (workspaceModule && isStaleModulePath(location.pathname, workspaceModule)) return;

      const resolved = sanitize(inferred ?? (!workspaceModule ? fallback : null));
      if (!resolved || workspaceModule === resolved) return;
      dispatch(setWorkspaceModule(resolved));
      return;
    }

    if (entryModule !== activeModule || workspaceModule !== activeModule) {
      dispatch(setWorkspaceModule(activeModule));
    }
  }, [
    owner,
    authLoading,
    workspaceModule,
    entryModule,
    activeModule,
    inferred,
    fallback,
    dispatch,
    location.pathname,
  ]);

  return activeModule;
}
